module Omni.Booker {

    enum TravelDatesState {
        SetArrival,
        SetDeparture
    }
    
    NgModule.component("omniBookerTravelDate", {
        bindings: {
            controlId: "@",
            travelDates: "=",    
            calendarClass: "@",   
            calendarMode: "@",
            calchange: "&",
            label: "@",
            isOpened: "=" 
        },
        controller: class OmniBookerTravelDateController {
            // For formatting, see https://momentjs.com/docs/#/displaying/format/ 
            static ShortDateFormat: string = "MM/DD/YYYY";  
            
            // Bindings
            public controlId: string;
            public travelDates: TravelDates;
            public calendarMode: string;
            public state: TravelDatesState;
            public weeks;
            public month;
            public day;
            public selected;
            public isOpened;
            public calchange: any;      
                                                       

            static $inject = ["$window", "$log", "moment", "omni.bookingservice"];
            constructor(private $window: ng.IWindowService, private $log: ng.ILogService,
                        private moment, private bookingService: Booker.IBookingService) {
                this.isOpened = false;
            }

            public $onInit() {
                const typedStateString = this.calendarMode as keyof typeof TravelDatesState;
                this.state = TravelDatesState[typedStateString];

                if (this.state == TravelDatesState.SetArrival) {
                    this.travelDates.ArrivalDate = this.getDefaultStartDate();
                }
                if (this.state == TravelDatesState.SetDeparture) {
                    this.travelDates.DepartureDate = this.getDefaultEndDate();
                }
            }
            
            public toggleCalendar() {
                this.isOpened = !this.isOpened;
                if (this.isOpened) {
                    this.syncCalendar();
                }
                this.calchange({ mode: this.calendarMode });
            }

            private getDefaultStartDate() {
                if (this.travelDates.ArrivalDate) {
                    return this.travelDates.ArrivalDate;
                } else {
                    return this.moment.utc(this.bookingService.GetCurrentDate());
                }
            }

            private getDefaultEndDate() {
                if (this.travelDates.DepartureDate) {
                    return this.travelDates.DepartureDate;
                } else {
                    return this.moment.utc(this.bookingService.GetCurrentDate()).add('days', 1);
                }
            }

            private syncCalendar() {
                this.selected = this._removeTime(this.moment.utc());
                this.$log.debug("syncing calendar=", this.state);

                if (this.state === TravelDatesState.SetDeparture && this.travelDates.DepartureDate) {
                    this.selected = this._removeTime(this.moment.utc(this.travelDates.DepartureDate));
                } else if (this.state === TravelDatesState.SetArrival && this.travelDates.ArrivalDate) {
                    this.selected = this._removeTime(this.moment.utc(this.travelDates.ArrivalDate));
                }

                this.month = this.selected.clone();

                const start = this.selected.clone();
                start.date(1);
                this._removeTime(start.day(0));

                this._buildMonth(start, this.month);
            }

            public isSelected(date) {
                // Used for styling the selected dates in calendar
              const arrival = this.moment.utc(this.travelDates.ArrivalDate);
              const departure = this.moment.utc(this.travelDates.DepartureDate);

              if (this.state === TravelDatesState.SetArrival && this.moment.utc(date).isSame(arrival)) {
                return true;
              }
              if (this.state === TravelDatesState.SetDeparture && this.moment.utc(date).isSame(departure)) {
                return true;
              }

              return false;
            }

            public select(day) {
                this.selected = day.date;
                this.$log.debug("selected=", this.selected);
            
                const today = this.moment.utc().startOf("day");
                if (this.selected.isBefore(today)) {
                    this.$log.debug("cannot set date in past");
                    return;
                }

                if (this.state === TravelDatesState.SetArrival) {
                    this.travelDates.ArrivalDate = this.selected;
                    if (this.travelDates.DepartureDate && (this.selected.isAfter(this.travelDates.DepartureDate)
                    || this.selected.isSame(this.travelDates.DepartureDate))) {
                        this.travelDates.DepartureDate = this.moment.utc(this.travelDates.ArrivalDate).add('days', 1);
                        this.syncToService();
                    }
                    if (day.isCurrentMonth === false) {
                        if (day.date.isAfter(this.month)) {
                            this.nextMonth();
                        }
                    }
                }

                if (this.state === TravelDatesState.SetDeparture) {
                    const arrival = this.moment.utc(this.travelDates.ArrivalDate);
                    if (arrival.isAfter(this.selected)) {
                        this.$log.debug("cannot set departure earlier than arrival");
                        return;
                    } else if (this.selected.isSame(arrival)) {
                        this.$log.debug("cannot set same dates");
                        return;
                    } else {
                        this.travelDates.DepartureDate = this.selected;
                        this.syncToService();
                    }
                }

                this.toggleCalendar();
            }

            public previousMonth() {        
                const prevMonth = this.month.clone().subtract("1", "months");
                const lastDay = prevMonth.endOf("month");

                // Disable previous month if its completely in the past
                if (this.moment.utc(lastDay).isBefore()) {
                    return;
                }

                // Select the previous month
                const previous = this.month.clone();
                this._removeTime(previous.month(previous.month() - 1).date(1));
                this.month.month(this.month.month() - 1);
                this._buildMonth(previous, this.month);
            }

            public nextMonth(keycode?: number) {        
                if (keycode && keycode === 9) {
                    this.$window.document.getElementById("prevMonth" + this.state).focus();
                }
                if (keycode && keycode === 13 || !keycode) {
                    const next = this.month.clone();
                    this._removeTime(next.month(next.month() + 1).date(1));
                    this.month.month(this.month.month() + 1);
                    this._buildMonth(next, this.month);
                }
            }

            private setTravelDateLabelFormat(date) {
                return this.moment.utc(date).format(OmniBookerTravelDateController.ShortDateFormat);
            }

            private syncToService() {
                this.bookingService.PutTravelDates(this.travelDates);
            }

            private _removeTime(date) {
                return date.day(0).hour(0).minute(0).second(0).millisecond(0);
            }

            private _buildMonth(start, month) {
                this.weeks = [];
                let done = false, date = start.clone(), monthIndex = date.month(), count = 0;
                while (!done) {
                    this.weeks.push({ days: this._buildWeek(date.clone(), month) });
                    date.add(1, "w");
                    done = count++ > 2 && monthIndex !== date.month();
                    monthIndex = date.month();
                }
            }

            private _buildWeek(date, month) {
                const days = [];
                for (let i = 0; i < 7; i++) {
                    days.push({
                        name: date.format("dd").substring(0, 1),
                        arialabel: date.format("MMMM Do YYYY"),
                        number: date.date(),
                        isCurrentMonth: date.month() === month.month(),
                        isToday: date.isSame(this.moment.utc(this.bookingService.GetCurrentDate()), "day"),
                        isPast: this.isIllegalSelectionDate(date),
                        date: date
                    });
                    date = date.clone();
                    date.add(1, "d");
                }
                return days;
            }

            private isIllegalSelectionDate(date) {
                if (date.isBefore(this.moment.utc(this.bookingService.GetCurrentDate()), "day")) {
                    return true;
                }

                return false;
            }
        },
        templateUrl: 'omni-booker-travel-date.html'
    });
}